import { BarChart, ArrowRight, Store, AlertCircle, Wand2, TrendingUp } from "lucide-react";
import { Card, CardContent } from '../../components/ui/card';
import { Button } from "../../components/ui/button";
import { useDispatch, useSelector } from "react-redux";
import { setIsGetStartedDialogOpen } from "../../store/flowSlice";
import { RootState } from "../../store/store";
import { compareDashboardUserState } from "../../store/userSlice";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "../../i18n/hooks";

type JobsEmptyViewProps = {}

const JobsEmptyView = ({}: JobsEmptyViewProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dashboardUserState = useSelector((state: RootState) => state.user.currentCollection.dashboardUserState);

  return (
    <div className="max-w-5xl mx-auto px-4 py-12 space-y-10">
      {/* Hero Section */}
      <Card className="border-0 shadow-lg overflow-hidden bg-gradient-to-r from-blue-600 to-indigo-700">
        <CardContent className="p-8 text-center space-y-6">
          {/* Hero Icon */}
          <div className="relative inline-block mx-auto">
            <div className="w-20 h-20 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center">
              <BarChart className="w-10 h-10 text-white" />
            </div>
            <div className="absolute top-0 right-0 translate-x-1/2 -translate-y-2">
              <div className="bg-green-400 rounded-full p-2">
                <ArrowRight className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h1 className="text-2xl md:text-2xl font-semibold tracking-tight text-white">
              {t('jobsEmptyView.start_first_job')}
            </h1>
            <p className="text-base md:text-lg text-blue-100 max-w-2xl mx-auto">
              {compareDashboardUserState(dashboardUserState, "importedProducts") >= 0 
                ? t('jobsEmptyView.products_need_work')
                : t('jobsEmptyView.connect_your_store_description')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Main Action Cards */}
      <div className="grid gap-6">
        {/* Initial State Card */}
        {compareDashboardUserState(dashboardUserState, "initial") === 0 && (
          <Card className="overflow-hidden border-0 shadow-lg bg-white">
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="bg-blue-50 w-12 h-12 rounded-lg flex items-center justify-center">
                  <Store className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{t('jobsEmptyView.connect_your_store')}</h3>
                  <p className="text-gray-600 mt-2">
                    {t('jobsEmptyView.sync_shopify_products')}
                  </p>
                </div>
                <Button
                  onClick={() => dispatch(setIsGetStartedDialogOpen(true))}
                  className="w-full sm:w-auto flex items-center justify-center gap-2"
                >
                  {t('jobsEmptyView.connect_shopify')}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Products Imported State Card */}
        {compareDashboardUserState(dashboardUserState, "importedProducts") >= 0 && (
          <Card className="overflow-hidden border-0 shadow-lg bg-white">
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="bg-blue-50 w-12 h-12 rounded-lg flex items-center justify-center">
                  <Wand2 className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{t('jobsEmptyView.ready_to_enhance')}</h3>
                  <p className="text-gray-600 mt-2">
                    {t('jobsEmptyView.store_connected_ready')}
                  </p>
                </div>
                <Button 
                  className="w-full sm:w-auto flex items-center justify-center gap-2" 
                  onClick={() => {
                    navigate('/admin/products');
                  }}  
>
                  {t('jobsEmptyView.start_enhancement')}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Card */}
        <Card className="border-0 shadow-md bg-white">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
              <div>
                <div className="font-medium text-gray-900">{t('jobsEmptyView.results_that_matter')}</div>
                <p className="mt-2 text-gray-600">
                  {t('jobsEmptyView.results_description')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* How It Works Section */}
      <Card className="border-0 shadow-md bg-white">
        <CardContent className="p-8">
          <div className="space-y-8">
            <h2 className="text-2xl font-semibold text-center text-gray-900">
              {t('jobsEmptyView.three_steps_better_performance')}
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="space-y-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto">
                  <Store className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-medium text-lg text-center">{t('jobsEmptyView.deep_product_analysis')}</h3>
                <p className="text-gray-600 text-center text-sm">
                  {t('jobsEmptyView.deep_analysis_description')}
                </p>
              </div>

              <div className="space-y-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto">
                  <Wand2 className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-medium text-lg text-center">{t('jobsEmptyView.ai_expert_enhancement')}</h3>
                <p className="text-gray-600 text-center text-sm">
                  {t('jobsEmptyView.ai_enhancement_description')}
                </p>
              </div>

              <div className="space-y-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto">
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-medium text-lg text-center">{t('jobsEmptyView.one_click_growth')}</h3>
                <p className="text-gray-600 text-center text-sm">
                  {t('jobsEmptyView.one_click_description')}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default JobsEmptyView;